import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight, PackageOpen, ShoppingBag } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useToast } from "../context/ToastContext";
import * as ordersApi from "../api/ordersApi";
import { formatDate, formatMoney, formatOrderStatus } from "../utils/format";
import "./Orders.css";

const PAGE_SIZE = 8;

export default function Orders() {
  const { userId } = useAuth();
  const { t, language } = useLanguage();
  const { addToast } = useToast();
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await ordersApi.getOrders(userId, page, PAGE_SIZE);
        if (!cancelled) setOrders(Array.isArray(data?.items) ? data.items : []);
      } catch (e) {
        if (!cancelled) addToast(t("orders.loadError"), "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, page, addToast, t]);

  const getStatusBadgeClass = (status) => {
    const s = String(status).toLowerCase();
    if (s.includes("confirm") || s === "2") return "orders-badge--confirmed";
    if (s.includes("ship") || s === "4") return "orders-badge--shipping";
    if (s.includes("deliver") || s.includes("complet") || s === "5") return "orders-badge--delivered";
    if (s.includes("cancel") || s === "6") return "orders-badge--cancelled";
    return "orders-badge--pending";
  };

  return (
    <div className="orders-page">
      <div className="container orders-page__inner">
        <Link to="/account" className="form-back btn-ghost">
          <ArrowLeft size={15} className="rtl-flip" />
          <span>{t("orders.back")}</span>
        </Link>
        <p className="section-label">{t("orders.sectionLabel")}</p>
        <h1 className="orders-page__title">{t("orders.title")}</h1>

        {loading && (
          <div className="page-loading">
            <span className="page-loading__dot" />
          </div>
        )}

        {!loading && orders.length === 0 && (
          <div className="orders-empty-card">
            <PackageOpen size={48} className="orders-empty-icon" />
            <h3 className="orders-empty-title">{t("orders.empty")}</h3>
            <p className="orders-empty-sub">{t("orders.emptySub")}</p>
            <Link to="/shop" className="btn-primary" style={{ marginTop: 20 }}>
              <ShoppingBag size={15} />
              <span>{t("orders.startShopping")}</span>
            </Link>
          </div>
        )}

        {!loading && orders.length > 0 && (
          <ul className="orders-list">
            {orders.map((o) => {
              const statusText = formatOrderStatus(o.orderStatus, language);
              return (
                <li key={o.id}>
                  <Link to={`/account/orders/${o.id}`} className="orders-row">
                    <div className="orders-row__left">
                      <span className="orders-row__id">
                        {t("orders.orderNum")} #{String(o.id).slice(0, 8)}
                      </span>
                      <span className="orders-row__date">{formatDate(o.createdAt, language)}</span>
                    </div>

                    <div className="orders-row__mid">
                      <span className={`orders-badge ${getStatusBadgeClass(o.orderStatus)}`}>
                        {statusText}
                      </span>
                      <span className="orders-row__price">{formatMoney(o.totalPrice, language)}</span>
                    </div>

                    <ChevronRight size={18} className="orders-row__chev rtl-flip" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}

        {!loading && (orders.length >= PAGE_SIZE || page > 1) && (
          <div className="orders-pager">
            <button
              type="button"
              className="btn-ghost"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t("orders.previous")}
            </button>
            <span className="orders-pager__label">
              {t("orders.page", { page: String(page) })}
            </span>
            <button
              type="button"
              className="btn-ghost"
              disabled={orders.length < PAGE_SIZE}
              onClick={() => setPage((p) => p + 1)}
            >
              {t("orders.next")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
